import React, { type MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { gsap } from 'gsap';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import LoadingIcon from '@pkg/assets/icons/v2/loading-bars.svg?react';
import PlusCircleIcon from '@pkg/assets/icons/v2/plus-circle.svg?react';
import { DepButton, DepButtonDisplay } from '@pkg/components/deprecated';
import { useAppDispatch, useAppSelector, useAuthentication, useDraftUser, useEmojiWasm } from '@pkg/hooks';
import { AuthRedirectType } from '@pkg/hooks/authentication/types';
import { useGetCart, useUpdateCart, useUpdateCartItems } from '@pkg/hooks/queryHooks/useCart.ts';
import { useSearchEmoji } from '@pkg/hooks/queryHooks/useSearchEmoji.ts';
import { useIsMounted } from '@pkg/hooks/useIsMounted.ts';
import { EmojiIdAvailability } from '@pkg/types';
import { Events } from '@pkg/types/app/events.ts';

import config from '@app/config';
import { getGlowLevel, type GlowLevelTypes } from '@app/helpers';
import { YAT_PICKER, YAT_PICKER_CHECKOUT } from '@app/routes/paths';
import { RoutingSearchParams } from '@app/routes/types';
import { registerEvent } from '@app/store/actions/events';
import { setRhythmScore as actionSetRhythmScore, setRhythmScoreCopy, updateEmojis } from '@app/store/actions/picker';
import { addAlert } from '@app/store/slices';
import { useAuthSidebar, useCartStore, UserType, useUser } from '@app/store/zustand';
import { useAuthTokens } from '@app/store/zustand/auth/useAuthTokens';

import InputStateBlurButton from '../InputStateToggles/InputStateBlurButton';
import InputStateFocusButton from '../InputStateToggles/InputStateFocusButton';

import './PickerResult.scss';
import { PickerResultRhythmScore } from './PickerResultRhythmScore';

interface PickerResultProps {
    onRhythmScoreClick?: (e) => void | null;
    preEmojiId?: string;
    walletKey?: string;
}

export const PickerResult = ({ onRhythmScoreClick, walletKey, preEmojiId }: PickerResultProps) => {
    const { t } = useTranslation(['yat_picker']);
    const { state: locationState } = useLocation();
    const dispatch = useAppDispatch();

    const { mutateAsync: updateCart } = useUpdateCart();
    const { data: cartData, refetch, isLoading } = useGetCart();
    const searchEmoji = useSearchEmoji();
    const setCart = useCartStore((s) => s.setCart);
    const orderItems = useCartStore((s) => s.validOrderItems);

    const nodeRef = useRef(null);
    const [searchParams] = useSearchParams();
    const preYat = useMemo(() => searchParams.get('yat'), [searchParams]);

    const isAuthenticated = useAuthTokens((state) => !!state.token.access_token);
    const { authenticate } = useAuthentication();
    const { type: userType } = useUser();
    const createDraftUser = useDraftUser();
    const isMounted = useIsMounted();

    const { mutateAsync: removeOrderItem } = useUpdateCartItems();

    const isRegisteredUser = useAppSelector((rootState) => rootState.user.isRegisteredUser);
    const features = useAppSelector((state) => state.featureFlags.features);

    const { emojiWasm: wasm } = useEmojiWasm();
    const { inputFocused, tiles, status } = useAppSelector((rootState) => rootState.picker);

    const [usedInitialTiles, setUsedInitialTiles] = useState(false);
    const [rhythmCategory, setRhythmCategory] = useState<GlowLevelTypes>('primary');
    const [loadingAddToCart, setLoadingAddToCart] = useState(false);
    const [yatComingSoon, setYatComingSoon] = useState(false);
    const [addedToCart, setAddedToCart] = useState(false);
    const [yatIsTaken, setYatIsTaken] = useState(false);
    const [showResult, setShowResult] = useState(true);
    const [rhythmScore, setRhythmScore] = useState(0);
    const [yatPrice, setYatPrice] = useState(0);
    const [buyNowYat, setBuyNowYat] = useState<string | null>(null);

    const addedToCartRef = useRef() as MutableRefObject<HTMLDivElement>;
    const navigate = useNavigate();
    const addToCartDelay = 1.5;

    const { isAuthSidebarOpen } = useAuthSidebar();

    const yat = useMemo(() => preEmojiId || tiles.map((tile) => tile[0]).join(''), [preEmojiId, tiles]);

    useEffect(() => {
        if (isLoading || !isAuthenticated) return;
        if (cartData) {
            setCart(cartData);
        } else {
            refetch().then((res) => {
                if (res.data) {
                    setCart(res.data);
                }
            });
        }
    }, [isLoading, cartData, refetch, setCart, isAuthenticated]);

    useEffect(() => {
        if (!features.yatInUrl) {
            return;
        }

        if (!usedInitialTiles && preYat && wasm && wasm?.is_valid_emoji_id(preYat)) {
            setUsedInitialTiles(true);
            const yat = new wasm.EmojiId(preYat, config.maxLengthYat);
            dispatch(updateEmojis(yat.splitCanonical().map((e) => [e, ''])));
        }
    }, [dispatch, wasm, usedInitialTiles, preYat, features.yatInUrl]);

    /* User has closed the auth sidebar after clicking on "Buy Now"
     * User Type = Still on draft mode (not authenticated)
     */
    useEffect(() => {
        if (!isAuthSidebarOpen && buyNowYat && userType === UserType.Draft) {
            setLoadingAddToCart(false);
            setBuyNowYat(null);

            const orderItem = orderItems.find((item) => item.emoji_id === buyNowYat);

            // Remove Yat from shopping cart
            if (orderItem?.id) {
                removeOrderItem({ id: orderItem.id });
            }
        }
    }, [buyNowYat, isAuthSidebarOpen, orderItems, removeOrderItem, userType]);

    const handleContactLink = useCallback(() => {
        window.location.href = config.requestEmojiLink;
    }, []);

    const handleAddToCart = useCallback(
        async ({ redirectPath = undefined, isBuyNow = false }: { redirectPath?: string; isBuyNow?: boolean } = {}) => {
            if (isBuyNow) {
                setBuyNowYat(yat);
            }

            if (status === 'YAT_UNDEFINED' && !preEmojiId) {
                return;
            }
            if (preEmojiId) {
                let compareEmojiId = preEmojiId;
                if (wasm) {
                    compareEmojiId = new wasm.EmojiId(preEmojiId, config.maxLengthYat).canonical();
                }
                if (orderItems.find((orderItem) => orderItem.emoji_id === compareEmojiId)) {
                    navigate('/checkout', { state: { walletLinkCreation: walletKey, enableGoBack: true } });
                    return;
                }
            }
            if (orderItems.find((orderItem) => orderItem.emoji_id === yat)) {
                dispatch(addAlert({ message: t('already_in_your_cart') }));
                return;
            }
            setLoadingAddToCart(true);
            if (!isBuyNow && !isAuthenticated) {
                // If Buy Now, draft user has already been created
                await createDraftUser();
            }
            const items = [{ emoji_id: yat }];

            updateCart({ items })
                .then((r) => {
                    if (!r.ok) {
                        setLoadingAddToCart(false);
                        return;
                    }

                    if (r.ok) {
                        setCart(r.value);
                    }
                })
                .finally(() => {
                    if (!isBuyNow) {
                        setTimeout(() => dispatch(updateEmojis([])), addToCartDelay * 1000);
                    }

                    if (redirectPath) {
                        navigate(redirectPath, {
                            state: { walletLinkCreation: walletKey },
                        });
                    } else {
                        setAddedToCart(true);
                    }
                });
        },
        [
            createDraftUser,
            dispatch,
            isAuthenticated,
            navigate,
            orderItems,
            preEmojiId,
            setCart,
            status,
            t,
            updateCart,
            walletKey,
            wasm,
            yat,
        ],
    );

    useEffect(() => {
        if (addedToCart && isMounted()) {
            gsap.timeline()
                .to(addedToCartRef.current, { autoAlpha: 1, y: 0, duration: 0.5, delay: 0.15, ease: 'power4.out' })
                .to(addedToCartRef.current, { autoAlpha: 0, y: 10, duration: 0.25, delay: addToCartDelay })
                .call(setAddedToCart, [false]);
        }
    }, [addedToCart, addedToCartRef, isMounted]);

    useEffect(() => {
        if (status !== 'YAT_UNDEFINED' && features.yatInUrl) {
            const urlSearchParams = new URLSearchParams();
            urlSearchParams.append('yat', yat);

            const ref = urlSearchParams.get('ref');
            if (ref) urlSearchParams.append('ref', ref);

            navigate(
                { search: urlSearchParams.toString() },
                { replace: true, state: { walletLinkCreation: walletKey, enableGoBack: true } },
            );
        }

        setShowResult(false);
        if (status === 'YAT_UNDEFINED') {
            if (!preEmojiId && !tiles.length) {
                dispatch(actionSetRhythmScore(0));
                return;
            }
        }

        setLoadingAddToCart(true);
        if (yat?.length) {
            searchEmoji(yat)
                .then((res) => {
                    if (res.ok && res.value) {
                        setRhythmScore(res.value.result.rhythm_score);
                        setRhythmCategory(getGlowLevel(res.value.result.rhythm_score));
                        dispatch(
                            setRhythmScoreCopy({
                                description: res.value.result.copy.description,
                                features: res.value.result.copy.features,
                            }),
                        );

                        switch (res.value.result.availability) {
                            // Yat NOT available
                            case EmojiIdAvailability.Taken:
                                setYatIsTaken(true);
                                break;

                            // Yat available
                            case EmojiIdAvailability.Available:
                            default:
                                setYatIsTaken(false);
                                break;
                        }

                        // Set Yat label as "Contact us"
                        setYatComingSoon(
                            !res.value.result.available &&
                                res.value.result.availability === EmojiIdAvailability.ComingSoon,
                        );

                        if (res.value.result.available) {
                            setYatPrice(res.value.result.price / 100);
                        }
                        setShowResult(true);

                        if (tiles.length >= config.analytics.searchLengthMin) {
                            registerEvent(Events.emoji_search_executed, {
                                query: yat,
                                result: res.value.result.availability,
                            });
                        }
                    } else {
                        setShowResult(false);
                    }
                })
                .finally(() => {
                    setLoadingAddToCart(false);
                });
        }
    }, [tiles, status, dispatch, navigate, preEmojiId, features.yatInUrl, walletKey, yat, searchEmoji]);

    useEffect(() => {
        setShowResult(status === 'YAT_DEFINED');
        if (status === 'YAT_UNDEFINED') {
            dispatch(actionSetRhythmScore(0));
        }
    }, [dispatch, status]);

    useEffect(() => {
        if (status === 'YAT_UNDEFINED') {
            dispatch(actionSetRhythmScore(0));
        }
        dispatch(actionSetRhythmScore(rhythmScore));
    }, [dispatch, rhythmScore, status]);

    const cartLimitHit = useMemo(() => {
        return orderItems.filter((oi) => oi.item_type === 'EmojiId').length >= config.cartLimit;
    }, [orderItems]);

    const buyNowButtonContent = useMemo(() => {
        if (yatIsTaken && !yatComingSoon) {
            return t('this_yat_is_taken');
        } else if (yatComingSoon) {
            return t('contact_us_for_this_yat');
        } else if (tiles.length < 3 && !preEmojiId) {
            return t('contact_us_for_this_yat');
        } else if (cartLimitHit) {
            return t('cart_limit_hit', { cartLimit: config.cartLimit });
        } else {
            return (
                <div>
                    {t('buy_now_for_price', {
                        price: yatPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                    })}
                    <span className="picker-result__hint-text">{t('pay_once_own_forever')}</span>
                </div>
            );
        }
    }, [yatComingSoon, yatIsTaken, tiles, yatPrice, cartLimitHit, t, preEmojiId]);

    const handleBuyNowClick = useCallback(async () => {
        if ((tiles.length < 3 && !preEmojiId) || yatComingSoon) {
            handleContactLink();
        } else {
            const checkoutPath = walletKey ? `/checkout?${RoutingSearchParams.PartnerKey}=${walletKey}` : '/checkout';
            const redirectPath = isRegisteredUser ? checkoutPath : '/create-new-user';

            if (!isRegisteredUser) {
                setLoadingAddToCart(true);
                await createDraftUser();
                handleAddToCart({ isBuyNow: true });
                authenticate({
                    redirect: walletKey
                        ? {
                              type: AuthRedirectType.Path,
                              path: `${YAT_PICKER_CHECKOUT}?${RoutingSearchParams.PartnerKey}=${walletKey}`,
                          }
                        : { type: AuthRedirectType.Checkout },
                });
            } else {
                handleAddToCart({ redirectPath });
            }
        }
    }, [
        authenticate,
        createDraftUser,
        handleAddToCart,
        handleContactLink,
        isRegisteredUser,
        preEmojiId,
        tiles.length,
        walletKey,
        yatComingSoon,
    ]);

    const buyNowButton = useMemo(() => {
        return (
            <DepButton
                variant={yatIsTaken ? 'muted' : rhythmCategory}
                loading={!showResult || loadingAddToCart}
                disabled={yatIsTaken || cartLimitHit}
                size="large"
                glow
                onClick={handleBuyNowClick}
                display={DepButtonDisplay.block}
            >
                {buyNowButtonContent}
            </DepButton>
        );
    }, [
        yatIsTaken,
        rhythmCategory,
        showResult,
        loadingAddToCart,
        cartLimitHit,
        handleBuyNowClick,
        buyNowButtonContent,
    ]);

    useEffect(() => {
        if (locationState?.afterDraftConvert) {
            //Reset the state so that subsequent refreshes will load the cart
            navigate(YAT_PICKER, { state: { afterDraftConvert: false, walletLinkCreation: walletKey }, replace: true });
            return;
        }

        if (!isAuthenticated) return;
    }, [locationState, navigate, dispatch, isAuthenticated, walletKey, setCart]);

    return (
        <div
            className={`picker-result${!tiles.length && !preEmojiId ? ' picker-result--hidden' : ''}${
                preEmojiId ? ' pre-populate' : ''
            }`}
        >
            <div className="picker-result__row">
                {!preEmojiId && (
                    <div className="picker-result__col picker-result__col--scores">
                        <div className="picker-result__scores">
                            <PickerResultRhythmScore
                                showScore={showResult}
                                score={rhythmScore}
                                onRhythmScoreClick={onRhythmScoreClick}
                            />
                        </div>
                    </div>
                )}
                <div className="picker-result__col picker-result__col--buttons">
                    <div className="picker-result__buttons">
                        {/* Buy now button */}
                        <CSSTransition
                            in={!!tiles.length || !!preEmojiId}
                            timeout={{ enter: 1250, exit: 500 }}
                            classNames="picker-result-button"
                            nodeRef={nodeRef}
                        >
                            <div className="picker-result__buy-now-button">
                                {tiles.length || !!preEmojiId ? (
                                    buyNowButton
                                ) : (
                                    <div className="picker-result__button-spacer" />
                                )}
                            </div>
                        </CSSTransition>
                        {/* Add to cart button */}
                        {!preEmojiId && (
                            <>
                                <button
                                    className={`picker-result__add-to-cart${
                                        !showResult ||
                                        yatIsTaken ||
                                        tiles.length < 3 ||
                                        yatComingSoon ||
                                        cartLimitHit ||
                                        addedToCart
                                            ? ' picker-result__add-to-cart--disabled'
                                            : ''
                                    }`}
                                    disabled={
                                        !showResult || yatIsTaken || tiles.length < 3 || yatComingSoon || cartLimitHit
                                    }
                                    onClick={() => handleAddToCart()}
                                >
                                    {t('add_to_cart')}
                                    {loadingAddToCart ? <LoadingIcon /> : <PlusCircleIcon />}
                                </button>
                                <div
                                    ref={addedToCartRef}
                                    className="picker-result__added-to-cart-notice"
                                    aria-hidden="true"
                                >
                                    {t('added')}
                                </div>
                            </>
                        )}
                    </div>
                </div>
                {!preEmojiId && (
                    <div className="picker-result__col picker-result__col--input-mode-toggle">
                        <div className="picker-result__input-mode-toggle">
                            <SwitchTransition>
                                <CSSTransition
                                    nodeRef={nodeRef}
                                    // key={inputFocused}
                                    timeout={750}
                                    classNames="picker-result-fade"
                                >
                                    {inputFocused ? <InputStateBlurButton /> : <InputStateFocusButton />}
                                </CSSTransition>
                            </SwitchTransition>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
